import chargerGen2 from "../../assets/charger_gen2.jpg";
import { getRelativeTime, formatDate } from "../../lib/utils/utility";

const CHARGER_IMAGES = [chargerGen2];

// Simple deterministic pick based on string hash so the same SKU always gets the same image
const getChargerImage = (value, row) => {
  const seed = row?.serialNumber || String(value) || "";
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return CHARGER_IMAGES[Math.abs(hash) % CHARGER_IMAGES.length];
};

// Country name → flag emoji mapping
const COUNTRY_FLAGS = {
  "united states": "🇺🇸",
  us: "🇺🇸",
  usa: "🇺🇸",
  "united kingdom": "🇬🇧",
  uk: "🇬🇧",
  gb: "🇬🇧",
  germany: "🇩🇪",
  de: "🇩🇪",
  france: "🇫🇷",
  fr: "🇫🇷",
  india: "🇮🇳",
  in: "🇮🇳",
  australia: "🇦🇺",
  au: "🇦🇺",
  canada: "🇨🇦",
  ca: "🇨🇦",
  japan: "🇯🇵",
  jp: "🇯🇵",
  china: "🇨🇳",
  cn: "🇨🇳",
  brazil: "🇧🇷",
  br: "🇧🇷",
  netherlands: "🇳🇱",
  nl: "🇳🇱",
  italy: "🇮🇹",
  it: "🇮🇹",
  spain: "🇪🇸",
  es: "🇪🇸",
  "south korea": "🇰🇷",
  kr: "🇰🇷",
  mexico: "🇲🇽",
  mx: "🇲🇽",
  sweden: "🇸🇪",
  se: "🇸🇪",
  norway: "🇳🇴",
  no: "🇳🇴",
  denmark: "🇩🇰",
  dk: "🇩🇰",
  finland: "🇫🇮",
  fi: "🇫🇮",
  switzerland: "🇨🇭",
  ch: "🇨🇭",
  belgium: "🇧🇪",
  be: "🇧🇪",
  austria: "🇦🇹",
  at: "🇦🇹",
  portugal: "🇵🇹",
  pt: "🇵🇹",
  ireland: "🇮🇪",
  ie: "🇮🇪",
  "new zealand": "🇳🇿",
  nz: "🇳🇿",
  singapore: "🇸🇬",
  sg: "🇸🇬",
};

const getCountryFlag = country => {
  if (!country) return "🌍";
  return COUNTRY_FLAGS[country.toLowerCase().trim()] || "🌍";
};

export const getColumns = (onSerialNumberClick = () => {}, onStationClick = () => {}) => [
  {
    label: "Serial Number",
    key: "serialNumber",
    filterable: true,
    render: (value, row) => {
      const { siteId, timezone } = row;
      return (
        <button className="serial-link" onClick={() => onSerialNumberClick(value, siteId, timezone)}>
          {value}
        </button>
      );
    },
  },
  { label: "Site ID", key: "siteId" },
  {
    label: "Station",
    key: "stationName",
    render: (value, row) => {
      const { stationId } = row;
      if (!stationId || value === "Unknown Station") {
        return <span>{value}</span>;
      }
      return (
        <button className="serial-link" onClick={() => onStationClick(stationId)}>
          {value}
        </button>
      );
    },
  },
  {
    label: "SKU",
    key: "sku",
    render: (value, row) => {
      if (!value) return "—";
      const img = getChargerImage(value, row);
      return (
        <span className="sku-cell">
          <img className="sku-cell__img" src={img} alt={value} />
          <span className="sku-cell__text">{value}</span>
        </span>
      );
    },
  },
  { label: "Protocol", key: "protocol" },
  { label: "Protocol Version", key: "protocolVersion" },
  { label: "Vendor Name", key: "vendorName" },
  { label: "Part Number", key: "partNum" },
  { label: "Model Name", key: "modelName" },
  { label: "Display Name", key: "displayName" },
  { label: "Reporting Interval (sec)", key: "reportingInterval" },
  { label: "Created At", key: "createdAt" },
  {
    label: "Last Reported At",
    key: "lastReportedAt",
    render: value => {
      if (!value) return "—";
      const rel = getRelativeTime(value);
      return (
        <span className="relative-time" title={formatDate(value)}>
          {rel}
        </span>
      );
    },
  },
  {
    label: "Offline At",
    key: "offlineAt",
    render: value => {
      if (!value) return "—";
      const rel = getRelativeTime(value);
      return (
        <span className="relative-time" title={formatDate(value)}>
          {rel}
        </span>
      );
    },
  },
  { label: "Updated At", key: "updatedAt" },
  { label: "Warranty Due Date", key: "warrantyDueDate" },
  { label: "Timezone", key: "timezone" },
  {
    label: "Country",
    key: "country",
    render: value => {
      if (!value) return "—";
      return (
        <span className="country-cell">
          <span className="country-cell__flag">{getCountryFlag(value)}</span>
          <span>{value}</span>
        </span>
      );
    },
  },
  {
    label: "Connection Status",
    key: "isConnected",
    render: value => {
      const connected = Boolean(value);
      return (
        <span className={`status-badge status-badge--${connected ? "connected" : "disconnected"}`}>
          <span className={`status-dot status-dot--${connected ? "connected" : "disconnected"}`} />
          {connected ? "Connected" : "Disconnected"}
        </span>
      );
    },
  },
  {
    label: "Retired",
    key: "isRetired",
    render: value => (value ? "Yes" : "No"),
  },
  {
    label: "Faulted",
    key: "isFaulted",
    render: value => (value ? "Yes" : "No"),
  },
  {
    label: "Firmware Version",
    key: "fwVersion",
    render: value => (value ? <span className="fw-chip">{value}</span> : "—"),
  },
  { label: "Firmware Updated At", key: "fwUpdatedAt" },
  { label: "Commissioning Status", key: "commissioningStatus" },
  { label: "Mode", key: "mode" },
  {
    label: "Partner Integration",
    key: "hoControl",
    render: value => (value ? "No" : "Yes"),
  },
  {
    label: "DLB Enabled",
    key: "dlbEnabled",
    render: value => (value ? "Yes" : "No"),
  },
  { label: "DLB Source", key: "dlbSource" },
  { label: "Phase Count", key: "phaseCount" },
  { label: "Max Current (A)", key: "maxCurrent" },
  {
    label: "Off-Peak Eligible",
    key: "isOffPeakEligible",
    render: value => (value ? "Yes" : "No"),
  },
  {
    label: "Default Off-Peak Eligible",
    key: "isDefaultOffPeakEligible",
    render: value => (value ? "Yes" : "No"),
  },
  {
    label: "Device Ready",
    key: "isDeviceReady",
    render: value => (value ? "Yes" : "No"),
  },
  {
    label: "Off Grid",
    key: "isOffGrid",
    render: value => (value ? "Yes" : "No"),
  },
];
